
import { useEffect, useRef, useCallback } from 'react'
import type { Node, Edge } from '@xyflow/react'
import type { ContextMenuTarget } from '@/hooks/canvas/useCanvasContextMenu'
import { Icon } from '@/components/ui/icon'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface MenuItem {
  id: string
  label: string
  shortcut?: string
  icon?: string
  destructive?: boolean
  disabled?: boolean
  onSelect: () => void
}

interface MenuSeparator {
  id: string
  type: 'separator'
}

type MenuEntry = MenuItem | MenuSeparator

function isSeparator(entry: MenuEntry): entry is MenuSeparator {
  return 'type' in entry && entry.type === 'separator'
}

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface CanvasContextMenuProps {
  menu: ContextMenuTarget | null
  onClose: () => void
  nodes: Node[]
  edges: Edge[]
  hasClipboard: boolean
  actions: {
    paste: () => void
    copySelected: () => void
    cut: () => void
    duplicateSelected: () => void
    deleteSelected: () => void
    selectAll: () => void
    toggleLock: () => void
    changeZIndex: (delta: number) => void
    addNode: (type: 'text' | 'image' | 'video') => void
    fitView: () => void
    deleteEdge: (edgeId: string) => void
    reverseEdge: (edgeId: string) => void
  }
}

/* ------------------------------------------------------------------ */
/*  Menu builders                                                      */
/* ------------------------------------------------------------------ */

function buildPaneMenu(actions: CanvasContextMenuProps['actions'], hasClipboard: boolean): MenuEntry[] {
  return [
    ...(hasClipboard ? [{
      id: 'paste',
      label: 'Paste',
      shortcut: '⌘V',
      icon: 'clipboard-paste',
      onSelect: actions.paste,
    }] : []),
    { id: 'sep-add', type: 'separator' as const },
    { id: 'add-text', label: 'Add Text', shortcut: 'T', icon: 'type', onSelect: () => actions.addNode('text') },
    { id: 'add-image', label: 'Add Image', shortcut: 'I', icon: 'image', onSelect: () => actions.addNode('image') },
    { id: 'add-video', label: 'Add Video', shortcut: 'V', icon: 'video', onSelect: () => actions.addNode('video') },
    { id: 'sep-view', type: 'separator' as const },
    { id: 'select-all', label: 'Select all', shortcut: '⌘A', icon: 'check-square', onSelect: actions.selectAll },
    { id: 'fit-view', label: 'Zoom to fit', shortcut: '⌘0', icon: 'maximize-2', onSelect: actions.fitView },
  ]
}

function buildNodeMenu(actions: CanvasContextMenuProps['actions'], node: Node): MenuEntry[] {
  const isLocked = !!(node.data as Record<string, unknown>)?.locked
  return [
    { id: 'rename', label: 'Rename', shortcut: 'F2', icon: 'pencil', onSelect: () => document.dispatchEvent(new CustomEvent('genie:rename-selected')) },
    { id: 'duplicate', label: 'Duplicate', shortcut: '⌘D', icon: 'copy', onSelect: actions.duplicateSelected },
    { id: 'copy', label: 'Copy', shortcut: '⌘C', icon: 'clipboard-copy', onSelect: actions.copySelected },
    { id: 'cut', label: 'Cut', shortcut: '⌘X', icon: 'scissors', onSelect: actions.cut },
    { id: 'sep-layer', type: 'separator' as const },
    { id: 'bring-forward', label: 'Bring forward', shortcut: '⌘]', icon: 'arrow-up', onSelect: () => actions.changeZIndex(1) },
    { id: 'send-backward', label: 'Send backward', shortcut: '⌘[', icon: 'arrow-down', onSelect: () => actions.changeZIndex(-1) },
    { id: 'lock', label: isLocked ? 'Unlock' : 'Lock', shortcut: '⌘L', icon: isLocked ? 'unlock' : 'lock', onSelect: actions.toggleLock },
    { id: 'sep-delete', type: 'separator' as const },
    { id: 'delete', label: 'Delete', shortcut: '⌫', icon: 'trash-2', destructive: true, onSelect: actions.deleteSelected },
  ]
}

function buildEdgeMenu(actions: CanvasContextMenuProps['actions'], edgeId: string): MenuEntry[] {
  return [
    { id: 'reverse', label: 'Reverse direction', icon: 'arrow-left-right', onSelect: () => actions.reverseEdge(edgeId) },
    { id: 'sep-delete', type: 'separator' as const },
    { id: 'delete-edge', label: 'Delete edge', icon: 'trash-2', destructive: true, onSelect: () => actions.deleteEdge(edgeId) },
  ]
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function CanvasContextMenu({ menu, onClose, nodes, hasClipboard, actions }: CanvasContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Close on click outside or Escape
  useEffect(() => {
    if (!menu) return

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as HTMLElement)) {
        onClose()
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    // Delay to avoid closing immediately from the same right-click event
    requestAnimationFrame(() => {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    })

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [menu, onClose])

  // Adjust position to stay within viewport
  useEffect(() => {
    if (!menu || !menuRef.current) return
    const el = menuRef.current
    const rect = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight

    if (rect.right > vw) {
      el.style.left = `${menu.position.x - rect.width}px`
    }
    if (rect.bottom > vh) {
      el.style.top = `${menu.position.y - rect.height}px`
    }
  }, [menu])

  const handleSelect = useCallback((entry: MenuItem) => {
    onClose()
    entry.onSelect()
  }, [onClose])

  if (!menu) return null

  let entries: MenuEntry[]
  if (menu.type === 'pane') {
    entries = buildPaneMenu(actions, hasClipboard)
  } else if (menu.type === 'node') {
    const node = nodes.find((n) => n.id === menu.nodeId)
    entries = node ? buildNodeMenu(actions, node) : []
  } else {
    entries = buildEdgeMenu(actions, menu.edgeId)
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-50 min-w-[180px] bg-popover border border-border rounded-lg shadow-lg py-1 animate-in fade-in zoom-in-95 duration-100"
      style={{ left: menu.position.x, top: menu.position.y }}
    >
      {entries.map((entry) =>
        isSeparator(entry) ? (
          <div key={entry.id} className="h-px bg-border mx-1 my-1" />
        ) : (
          <button
            key={entry.id}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-accent rounded-sm mx-0 transition-colors ${
              entry.destructive ? 'text-destructive hover:text-destructive' : 'text-popover-foreground'
            } ${entry.disabled ? 'opacity-50 pointer-events-none' : ''}`}
            onClick={() => handleSelect(entry as MenuItem)}
            disabled={entry.disabled}
          >
            {entry.icon && <Icon name={entry.icon as Parameters<typeof Icon>[0]['name']} className="size-4 text-muted-foreground shrink-0" />}
            <span className="flex-1">{entry.label}</span>
            {entry.shortcut && (
              <span className="text-muted-foreground text-xs ml-auto pl-4">{entry.shortcut}</span>
            )}
          </button>
        ),
      )}
    </div>
  )
}
